import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";

import TravelHeader from "../../components/travel/Header";
import TravelInput from "../../components/travel/Input";
import TravelDobPicker from "../../components/travel/DobPicker";
import TravelButton from "../../components/travel/Button";
import TravelCheckbox from "../../components/travel/Checkbox";
import TravelOtpModal from "../../components/travel/OtpModal";
import TravelVerifyLoader from "../../components/travel/VerifyLoader";
import TravelThankYou from "./ThankYou";
import "./index.css";

import {
  travelSendOtp,
  travelVerifyOtp,
  travelQecProfileIngestion,
  extractApiError,
} from "../../utils/travel-api";
import DataLakeTracker from "../../utils/tracking/data-lake-tracker";
import { fetchDeviceId } from "../../utils/helper";
import { v4 as uuidv4 } from "uuid";

// ─── Validation ───────────────────────────────────────────────────────────────
function validateText(val) {
  return typeof val === "string" && /^[a-zA-Z\s]+$/.test(val.trim()) && val.trim().length > 0;
}
function validateMobile(val) { return /^[6-9]\d{9}$/.test(val); }
function validateDob(val) {
  if (!val) return false;
  const birthDate = new Date(val);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age >= 20;
}

// ─── Feature item ─────────────────────────────────────────────────────────────
function FeatureItem({ iconSrc, iconAlt, title, subtitle }) {
  return (
    <div className="tv-feat">
      <div className="tv-feat-icon">
        <img src={iconSrc} alt={iconAlt} />
      </div>
      <div>
        <div className="tv-feat-title">{title}</div>
        <div className="tv-feat-sub">{subtitle}</div>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function TravelQRJourney() {
  const merchantDetail = useSelector((state) => state.auth.merchantDetails);
  const params = new URLSearchParams(window.location.search);
  const mId = params.get("mId");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [mobile, setMobile] = useState("");
  const [dob, setDob] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [errors, setErrors] = useState({});
  const [isApiLoading, setIsApiLoading] = useState(false);
  const [otpOpen, setOtpOpen] = useState(false);
  const [isVerifyLoading, setIsVerifyLoading] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [toast, setToast] = useState({ open: false, msg: "", type: "info" });

  useEffect(() => {
    DataLakeTracker({
      logEventType: "device_info",
      eventName: "qr_journey_started",
      eventValue: fetchDeviceId(),
      finallyFn: () => {},
    });
  }, []);

  const showToast = (msg, type = "info") => {
    setToast({ open: true, msg, type });
    setTimeout(() => setToast((t) => ({ ...t, open: false })), 4000);
  };

  const handleOtpVerify = (otp) => {
    setIsVerifyLoading(true);
    travelVerifyOtp({
      mobile, otp,
      utmResource: merchantDetail?.utmSource,
      medium: merchantDetail?.journeyType,
      userId: params.get("userId") || undefined,
      successFn: () => {
        const merchantId = parseInt(mId, 10);
        travelQecProfileIngestion({
          payload: {
            customerConsent: true, isQR: true,
            personalInfo: { mobileNumber: mobile, firstName, lastName, dob },
            orderInfo: { orderAmount: "0", channelName: merchantId },
            isRetry: false, platformSource: null,
          },
          successFn: () => { setIsVerifyLoading(false); setOtpOpen(false); setShowThankYou(true); },
          errorFn: (err) => { setIsVerifyLoading(false); setOtpOpen(false); showToast(extractApiError(err, "Profile creation failed. Please try again."), "error"); },
        });
      },
      errorFn: (err) => { setIsVerifyLoading(false); showToast(extractApiError(err, "Invalid OTP. Please try again."), "error"); },
    });
  };

  const handleSubmit = () => {
    const newErrors = {};
    if (!validateText(firstName) || firstName.trim().length < 2) {
      newErrors.firstName = "Please enter a valid first name (min 2 characters)";
    } else if (firstName.trim().length > 50) {
      newErrors.firstName = "First name cannot exceed 50 characters";
    }

    if (!validateText(lastName) || lastName.trim().length < 2) {
      newErrors.lastName = "Please enter a valid last name (min 2 characters)";
    } else if (lastName.trim().length > 50) {
      newErrors.lastName = "Last name cannot exceed 50 characters";
    }

    if (!validateMobile(mobile)) newErrors.mobile = "Please enter a valid 10-digit mobile number";
    if (!validateDob(dob)) newErrors.dob = "Min age should be 20 years old";
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }
    if (!mId || isNaN(parseInt(mId, 10))) { showToast("Invalid link. Merchant ID is missing or invalid.", "error"); return; }
    setIsApiLoading(true);
    travelSendOtp({
      mobile, merchantId: parseInt(mId, 10), userId: parseInt(params.get("userId"), 10) || undefined,
      successFn: () => { setOtpOpen(true); setErrors({}); showToast("OTP sent to your mobile number.", "success"); },
      errorFn: (err) => { showToast(extractApiError(err, "Failed to send OTP. Please try again."), "error"); },
      finallyFn: () => setIsApiLoading(false),
    });
  };

  const handleResendOtp = () => {
    DataLakeTracker({
      logEventType: "device_info",
      eventName: "resend_otp_button_clicked",
      eventValue: uuidv4(),
      Authorization: null,
      customerId: null,
      customerRefNum: null,
      finallyFn: () => {},
    });
    travelSendOtp({
      mobile, merchantId: parseInt(mId, 10), userId: parseInt(params.get("userId"), 10) || undefined,
      isResendOtp: true,
      successFn: () => showToast("OTP resent.", "success"),
      errorFn: (err) => showToast(extractApiError(err, "Failed to resend OTP."), "error"),
    });
  };

  const isFormValid = firstName.trim().length > 0 && lastName.trim().length > 0 && mobile.length === 10 && dob && agreed && !isApiLoading;

  const handleThankYouReset = () => {
    DataLakeTracker({
      logEventType: "device_info",
      eventName: "journey_ended",
      eventValue: uuidv4(),
      Authorization: null,
      customerId: null,
      customerRefNum: null,
      finallyFn: () => {},
    });
    setShowThankYou(false);
    setFirstName("");
    setLastName("");
    setMobile("");
    setDob("");
    setAgreed(false);
  };

  if (showThankYou) {
    return (
      <TravelThankYou
        onReset={handleThankYouReset}
      />
    );
  }

  const toastClassName = `tv-toast tv-toast--${toast.type === "error" || toast.type === "success" ? toast.type : "info"}`;

  return (
    <>
      <div className="tv-root">
        <TravelVerifyLoader show={isVerifyLoading} />
        <TravelOtpModal
          isOpen={otpOpen}
          onClose={() => setOtpOpen(false)}
          onVerify={handleOtpVerify}
          onResend={handleResendOtp}
          mobile={mobile}
          isLoading={isVerifyLoading}
        />

        {toast.open && (
          <div className={toastClassName}>{toast.msg}</div>
        )}

        <TravelHeader />

        <div className="tv-container">
          {/* ── Left banner ── */}
          <div className="tv-left">
            {/* Badge */}
            <div className="tv-badge">
              <img src="/assets/traval-icon.svg" alt="travel" className="tv-badge-icon" />
              <span className="tv-badge-text">Travel now Pay later</span>
            </div>

            {/* Dashed divider */}
            <div className="tv-divider" />

            {/* Partner */}
            <p className="tv-partner">
              Travel partner :{" "}
              <span className="tv-partner-name">{merchantDetail?.displayName || "NA"}</span>
            </p>

            {/* Heading */}
            <h1 className="tv-heading">
              Make dream travel with{" "}
              <span className="tv-heading-bold">Fibe EMIS</span>
            </h1>

            {/* Loan amount */}
            <p className="tv-loan">Loan up to ₹10,00,000</p>

            {/* Features */}
            <div className="tv-features">
              <FeatureItem iconSrc="/assets/digital.svg" iconAlt="100% digital" title="100% digital" subtitle="process" />
              <FeatureItem iconSrc="/assets/low-cost.svg" iconAlt="Low cost" title="Low cost" subtitle="easy EMIS" />
              <FeatureItem iconSrc="/assets/forclouser.svg" iconAlt="0 Foreclosure" title="0 Foreclosure" subtitle="charges" />
            </div>

            {/* Illustration */}
            <div className="tv-illus">
              <img src="/assets/plain-img.svg" alt="Travel Illustration" />
            </div>
          </div>

          {/* Doted separator (mobile only) */}
          <img src="/assets/doted-bg.svg" alt="" className="tv-doted" />

          {/* ── Right form ── */}
          <div className="tv-right">
            <h2 className="tv-form-title">Share details for instant travel EMI</h2>

            <div className="tv-field">
              <TravelInput id="firstName" name="firstName" title="First name (as per PAN)" placeholder="Enter first name" inputValue={firstName} maxLength={50}
                onChange={(e) => {
                  setFirstName(e.target.value);
                  setErrors((err) => ({ ...err, firstName: undefined }));
                }}
                isValid={!errors.firstName} errorText={errors.firstName} isDisabled={isApiLoading} />
            </div>

            <div className="tv-field">
              <TravelInput id="lastName" name="lastName" title="Last name (as per PAN)" placeholder="Enter last name" inputValue={lastName} maxLength={50}
                onChange={(e) => {
                  setLastName(e.target.value);
                  setErrors((err) => ({ ...err, lastName: undefined }));
                }}
                isValid={!errors.lastName} errorText={errors.lastName} isDisabled={isApiLoading} />
            </div>

            <div className="tv-field">
              <TravelInput id="mobile" name="mobile" type="tel" title="Mobile number" placeholder="Enter 10-digit mobile number" inputValue={mobile} maxLength={10}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, "").slice(0, 10);
                  setMobile(v);
                  setErrors((err) => ({ ...err, mobile: undefined }));
                }}
                isValid={!errors.mobile} errorText={errors.mobile} isDisabled={isApiLoading} />
            </div>

            <div className="tv-field">
              <TravelDobPicker id="dob" name="dob" title="Date of birth (as per PAN)" placeholder="DD/MM/YYYY" inputValue={dob}
                onChange={(e) => {
                  setDob(e.target.value);
                  setErrors((err) => ({ ...err, dob: undefined }));
                }}
                isValid={!errors.dob} errorText={errors.dob} isDisabled={isApiLoading} />
            </div>

            <div className="tv-consent">
              <div className="tv-consent-checkbox">
                <TravelCheckbox id="agreed" isChecked={agreed} onChange={() => setAgreed((v) => !v)} isDisabled={isApiLoading} />
              </div>
              <label htmlFor="agreed" className="tv-consent-label">
                I accept the{" "}
                <a href="https://webviews.fibe.in/terms-and-conditions?src=mobile" target="_blank" rel="noopener noreferrer">
                  terms of service
                </a>{" "}
                &{" "}
                <a href="https://webviews.fibe.in/privacy-policy?src=web" target="_blank" rel="noopener noreferrer">
                  privacy policy
                </a>{" "}
                which includes permission to securely share my data/documents with verified{" "}
                <a href="https://www.fibe.in/our-lending-partners/" target="_blank" rel="noopener noreferrer">
                  partners
                </a>{" "}
                to process my application & communicate with you via phone, e-mails, WhatsApp, etc.
              </label>
            </div>

            <TravelButton variant="primary" clickText={isApiLoading ? "Sending..." : "Submit"} disabled={!isFormValid} onClick={handleSubmit} id="request-otp-btn" />
          </div>
        </div>
      </div>
    </>
  );
}
